import { useDispatch, useSelector, type TypedUseSelectorHook } from "react-redux";
import { useEffect } from "react";
import type { RootState, AppDispatch } from "./store";
import { setResume, initialResumeState } from "./resumeSlice";
import { changeSettings } from "./settingsSlice";
import type { Resume } from "./types";
import type { ResumeSettings } from "./settingsSlice";

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// Hook to save state to localStorage on changes
export const useSaveStateToLocalStorageOnChange = () => {
  const resume = useAppSelector((state) => state.resume);
  const settings = useAppSelector((state) => state.settings);

  useEffect(() => {
    localStorage.setItem("resume", JSON.stringify(resume));
  }, [resume]);

  useEffect(() => {
    localStorage.setItem("settings", JSON.stringify(settings));
  }, [settings]);
};

// Hook to set initial store from localStorage
export const useSetInitialStore = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    const savedResume = localStorage.getItem("resume");
    const savedSettings = localStorage.getItem("settings");

    if (savedResume) {
      try {
        const parsedResume: Resume = JSON.parse(savedResume);
        dispatch(setResume(parsedResume));
      } catch (error) {
        console.error("Failed to parse saved resume:", error);
        dispatch(setResume(initialResumeState));
      }
    }

    if (savedSettings) {
      try {
        const parsedSettings: ResumeSettings = JSON.parse(savedSettings);
        dispatch(changeSettings({ field: "theme", value: parsedSettings.theme }));
        dispatch(changeSettings({ field: "formsOrder", value: parsedSettings.formsOrder }));
      } catch (error) {
        console.error("Failed to parse saved settings:", error);
      }
    }
  }, [dispatch]);
};
